<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useAcl } from "vue-simple-acl";
import type LxcTabsWithNavigation from "~/core/components/TabsWithNavigation.vue";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import { PATH_BUSINESS_UNITS_ID } from "~/modules/businessUnit/constants";
import { PATH_DASHBOARD } from "~/modules/dashboard/constants";
import {
  PATH_ORGANIZATIONS_ID_SETTINGS_INFORMATION,
  PATH_ORGANIZATIONS_ID_SETTINGS_LOGS,
  PATH_ORGANIZATIONS_ID_SETTINGS_USERS,
} from "~/modules/organization/constants";
import { OrganizationType } from "~/modules/organization/models/OrganizationType.enum";
import type { OrganizationV2 } from "~/modules/organization/models/OrganizationV2.interface";
import organizationService from "~/modules/organization/services/organization.service";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";
import ILxcExternalLink from "~icons/lxc/external-link";
import ILxcPlus from "~icons/lxc/plus";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const confirmLeavePageStore = useConfirmLeavePageStore();
const { userSession } = storeToRefs(useUserSession());
const { can } = useAcl();

const organizationCode: Ref<number> = ref(
  parseInt(route.params.id as string, 10),
);
const isLoading: Ref<boolean> = ref(false);
const error: Ref<LxcError | null> = ref(null);
const organization: Ref<OrganizationV2 | undefined> = ref(undefined);
const currentSelectedUser: Ref<string | undefined> = ref(undefined);

const isOperationButtonShown: ComputedRef<boolean> = computed(() => {
  return (
    organization.value?.type === OrganizationType.OPERATOR &&
    organizationCode.value === userSession.value?.entity?.code
  );
});

const backDefaultRoute = computed(() =>
  userSession.value?.entity?.type ===
    UserSessionEntityType.SUPER_ADMINISTRATION ||
  userSession.value?.entity?.type === UserSessionEntityType.BUSINESS_UNIT
    ? router.resolve({
        name: PATH_BUSINESS_UNITS_ID,
        params: { id: organization.value?.businessUnitCode },
      })
    : undefined,
);

//
// Navigation section. Logic / configuration responsible for the navigation.
//
const tabsWithNavigationRef: Ref<typeof LxcTabsWithNavigation | undefined> =
  ref();

const descriptionTabId = "description";
const usersTabId = "users";
const logsTabId = "logs";

const usersTabRedirectPath = router.resolve({
  name: PATH_ORGANIZATIONS_ID_SETTINGS_USERS,
  params: { id: organizationCode.value },
}).href;

const tabNavigations: TabNavigation[] = [
  {
    id: descriptionTabId,
    redirectPath: router.resolve({
      name: PATH_ORGANIZATIONS_ID_SETTINGS_INFORMATION,
      params: { id: organizationCode.value },
    }).href,
    labelTranslationKey:
      "administration.organization.settings.information.title",
  },
];

if (can(ACLRoles.CAN_READ_USER)) {
  tabNavigations.push({
    id: usersTabId,
    redirectPath: usersTabRedirectPath,
    labelTranslationKey: "administration.organization.settings.users.title",
    forceRefresh: true,
  });
}

tabNavigations.push({
  id: logsTabId,
  redirectPath: router.resolve({
    name: PATH_ORGANIZATIONS_ID_SETTINGS_LOGS,
    params: { id: organizationCode.value },
  }).href,
  labelTranslationKey: "administration.organization.settings.logs.title",
  forceRefresh: true,
});

const selectedTabIndex: Ref<number | undefined> = ref();
const isUsersTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === usersTabId,
    ),
);
const showUserForm: Ref<boolean> = ref(false);
const showUserAdd: ComputedRef<boolean> = computed((): boolean => {
  return (
    isUsersTabSelected.value &&
    can(ACLRoles.CAN_WRITE_USER) &&
    !organization.value?.useExternalIdp
  );
});

async function fetchData() {
  isLoading.value = true;

  const response = await organizationService.getOrganization(
    organizationCode.value,
  );

  if (LxcError.check(response)) {
    error.value = response;
  } else {
    organization.value = response;
  }

  isLoading.value = false;
}

function initLeavePageStore() {
  const excludedRoutes: string[] = tabNavigations.map((tab) => {
    return tab.redirectPath;
  });
  confirmLeavePageStore.setExcludedRoutes(excludedRoutes);
  confirmLeavePageStore.setTitle("administration.organization.leave.title");
  confirmLeavePageStore.setConfirmMessage(
    "administration.organization.leave.message",
  );
}

function onAddUser() {
  currentSelectedUser.value = undefined;
  showUserForm.value = true;
}

function setUserForm() {
  const userLogin: string = route.params.userLogin as string;
  if (userLogin) {
    currentSelectedUser.value = userLogin;
    showUserForm.value = true;
  } else {
    showUserForm.value = false;
  }
}

function onOperatorClick() {
  router.push(PATH_DASHBOARD);
}

fetchData();

initLeavePageStore();

watch(route, setUserForm);

onMounted(setUserForm);

onUnmounted(() => {
  confirmLeavePageStore.reset();
});
</script>

<template>
  <page-component
    :name="
      organization
        ? t('page.organizations.title', {
            name: organization?.name,
          })
        : ''
    "
    :should-not-translate="true"
    :is-loading="isLoading"
    :error="error"
    :back-default-route="backDefaultRoute"
  >
    <template #top-right>
      <div class="flex gap-4">
        <template v-if="showUserAdd">
          <lxc-button
            :icon="ILxcPlus"
            :title="t('user.addUser.button')"
            class="self-center"
            html-type="button"
            type="primary"
            @click="onAddUser"
          >
            {{ t("user.addUser.button") }}
          </lxc-button>
        </template>
        <lxc-button
          v-if="isOperationButtonShown"
          :icon="ILxcExternalLink"
          :title="t('administration.organization.settings.operationButton')"
          class="self-center"
          html-type="button"
          type="secondary"
          @click="onOperatorClick"
        >
          LX CONNECT
        </lxc-button>
      </div>
    </template>

    <tabs-with-navigation
      ref="tabsWithNavigationRef"
      :tab-navigations="tabNavigations"
      @update:selected-tab-index="
        (newSelectedTabIndex) => (selectedTabIndex = newSelectedTabIndex)
      "
    >
      <template #[descriptionTabId]>
        <organization-description
          v-if="organization"
          :model-value="organization!"
        />
      </template>

      <template #[usersTabId]>
        <organization-users
          v-model:show-user-form="showUserForm"
          v-model:current-selected-user="currentSelectedUser"
          :organization-code="organizationCode"
          :organization-type="organization?.type"
          :path-context="usersTabRedirectPath"
          :use-external-idp="organization?.useExternalIdp"
        />
      </template>

      <template #[logsTabId]>
        <log-list
          :organization-code="organizationCode"
          :path-context="usersTabRedirectPath"
        />
      </template>
    </tabs-with-navigation>
  </page-component>
</template>
